import React, { useMemo } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $getRoot, LexicalNode, ParagraphNode } from 'lexical';
import { $convertFromMarkdownString } from '@lexical/markdown';
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { MarkdownTablePlugin } from '../../plugins/MarkdownTablePlugin';
import { ListBreakPlugin } from '../../plugins/ListBreakPlugin';
import { NestedListPlugin } from '../../plugins/NestedListPlugin';
import { $isDiffHunkNode, type DiffHunkNode } from './DiffHunkNode';
import { AttachmentNode } from '../AttachmentNode/AttachmentNode';
import { MermaidNode } from '../MermaidNode/MermaidNode';
import { LOAD_TRANSFORMERS } from '../../transformers';
import { theme } from '../../markdownEditorTheme';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useImpressionEditorContext } from '../../../Context';

type DiffLine = {
  type: 'added' | 'removed';
  content: string;
};

type DiffSegment = {
  type: 'added' | 'removed';
  markdown: string;
};

export const parseDiffContent = (diffContent: string): DiffLine[] => {
  const lines: DiffLine[] = [];
  for (const line of diffContent.split('\n')) {
    if (line.startsWith('+ ') || line === '+') {
      lines.push({ type: 'added', content: line.slice(2) });
    } else if (line.startsWith('- ') || line === '-') {
      lines.push({ type: 'removed', content: line.slice(2) });
    }
  }
  return lines;
};

/**
 * Replace a single DiffHunkNode with the resolved markdown, keeping
 * either the "added" or the "removed" side. Must be called from inside
 * a Lexical `editor.update()` callback.
 *
 * Shared between the inline per-hunk Accept/Reject buttons on this
 * component and the bulk Accept all / Reject all controls so both paths
 * produce identical output.
 */
export function $resolveDiffHunkInPlace(
  node: DiffHunkNode,
  keep: 'added' | 'removed',
): void {
  const lines = parseDiffContent(node.getDiffContent());
  const resolvedMarkdown = lines
    .filter(line => line.type === keep)
    .map(line => line.content)
    .join('\n');

  if (!resolvedMarkdown.trim()) {
    node.remove();
    return;
  }

  // Parse the resolved markdown into Lexical nodes by temporarily swapping
  // out the root's children: stash the current children, parse into an
  // empty root, pop the parsed nodes back out, restore the saved children,
  // then splice the parsed nodes in at the hunk's position.
  const root = $getRoot();
  const savedChildren = root.getChildren();
  root.clear();

  $convertFromMarkdownString(resolvedMarkdown, LOAD_TRANSFORMERS);

  const parsedNodes = [...root.getChildren()];
  root.clear();

  for (const child of savedChildren) {
    root.append(child);
  }

  let lastInserted: LexicalNode | null = null;
  for (const parsed of parsedNodes) {
    if (!lastInserted) {
      node.insertBefore(parsed);
    } else {
      lastInserted.insertAfter(parsed);
    }
    lastInserted = parsed;
  }

  node.remove();
}

export const groupDiffLines = (lines: DiffLine[]): DiffSegment[] => {
  const segments: DiffSegment[] = [];

  for (const line of lines) {
    const last = segments[segments.length - 1];
    if (last && last.type === line.type) {
      last.markdown += '\n' + line.content;
    } else {
      segments.push({ type: line.type, markdown: line.content });
    }
  }

  return segments;
};

const SEGMENT_NODES = [
  ParagraphNode,
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
  CodeNode,
  CodeHighlightNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  AttachmentNode,
  MermaidNode,
  HorizontalRuleNode,
];

const useStyles = makeStyles(muiTheme => ({
  container: {
    border: `1px solid ${muiTheme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    margin: '12px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    backgroundColor: muiTheme.palette.grey[100],
    borderBottom: `1px solid ${muiTheme.palette.divider}`,
  },
  headerLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: muiTheme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  segments: {
    lineHeight: 1.6,
  },
  segmentAdded: {
    backgroundColor: '#eafaf0',
    borderLeft: '3px solid #2da44e',
    padding: '8px 16px',
    '& + &': {
      borderTop: '1px solid #c3e6cb',
    },
  },
  segmentRemoved: {
    backgroundColor: '#fff5f5',
    borderLeft: '3px solid #cf222e',
    padding: '8px 16px',
    '& + &': {
      borderTop: '1px solid #f5c6cb',
    },
  },
  segmentContent: {
    width: '100%',
    outline: 'none',
    '& p': {
      margin: '0 0 4px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      marginTop: 4,
      marginBottom: 4,
      paddingLeft: 20,
    },
    '& table': {
      margin: '4px 0',
      fontSize: '0.9em',
    },
  },
}));

const DiffSegmentViewer: React.FC<{
  segment: DiffSegment;
  className: string;
  contentClassName: string;
}> = ({ segment, className, contentClassName }) => {
  const initialConfig: InitialConfigType = {
    namespace: `diff-segment-${segment.type}`,
    onError: err => console.error(err),
    nodes: SEGMENT_NODES,
    editorState: () =>
      $convertFromMarkdownString(segment.markdown, LOAD_TRANSFORMERS),
    editable: false,
    theme,
  };

  return (
    <div className={className} data-testid={`diff-segment-${segment.type}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={<ContentEditable className={contentClassName} />}
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={null}
        />
        <ListBreakPlugin />
        <NestedListPlugin />
        <TablePlugin />
        <MarkdownTablePlugin />
      </LexicalComposer>
    </div>
  );
};

type DiffHunkProps = {
  diffContent: string;
  nodeKey: string;
};

function hunkDetailPreview(diffContent: string, maxLen = 120): string {
  const oneLine = diffContent.replace(/\s+/g, ' ').trim();
  return oneLine.length <= maxLen ? oneLine : `${oneLine.slice(0, maxLen)}…`;
}

export const DiffHunk = ({ diffContent, nodeKey }: DiffHunkProps) => {
  const [editor] = useLexicalComposerContext();
  const classes = useStyles();
  const { recordSuggestionHunkDecision, activeImpression } =
    useImpressionEditorContext();

  const lines = useMemo(() => parseDiffContent(diffContent), [diffContent]);
  const segments = useMemo(() => groupDiffLines(lines), [lines]);

  const resolveHunk = (keep: 'added' | 'removed') => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node || !$isDiffHunkNode(node)) {
        return;
      }
      $resolveDiffHunkInPlace(node, keep);
    });
  };

  const handleAccept = () => {
    recordSuggestionHunkDecision({
      traceId: activeImpression.suggestionTraceId,
      decision: 'accepted',
      detail: hunkDetailPreview(diffContent),
    });
    resolveHunk('added');
  };
  const handleReject = () => {
    recordSuggestionHunkDecision({
      traceId: activeImpression.suggestionTraceId,
      decision: 'rejected',
      detail: hunkDetailPreview(diffContent),
    });
    resolveHunk('removed');
  };

  return (
    <div className={classes.container} data-testid="diff-hunk">
      <Box className={classes.header}>
        <span className={classes.headerLabel}>Requested change</span>
        <div className={classes.actions}>
          <Button
            variant="text"
            color="negative"
            onClick={handleReject}
            data-testid="diff-hunk-reject"
          >
            Reject
          </Button>
          <Button
            variant="outlined"
            color="default"
            onClick={handleAccept}
            data-testid="diff-hunk-accept"
          >
            Accept
          </Button>
        </div>
      </Box>
      <div className={classes.segments}>
        {segments.map((segment, i) => (
          <DiffSegmentViewer
            key={i}
            segment={segment}
            className={
              segment.type === 'added'
                ? classes.segmentAdded
                : classes.segmentRemoved
            }
            contentClassName={classes.segmentContent}
          />
        ))}
      </div>
    </div>
  );
};
